import React, { useState, useEffect } from 'react';
import useCart from '../hooks/useCart';
import ProductModal from '../components/ProductModal';
import Brands from '../components/Brands';
import PaymentMethods from "../components/PaymentMethods";
import '../styles/shop.css';
import ContactDetails from "../components/ContactDetails";
import { ScaleLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import {BASE_URL} from "../config";


const Shop = ({ showToast }) => {
    const { t }  = useTranslation();
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [tags, setTags] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 12,
        total: 0,
        last_page: 1
    });

    // Active filters (applied filters)
    const [activeFilters, setActiveFilters] = useState({
        tags: [],
        brands: [],
        minPrice: '',
        maxPrice: '',
        search: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    // Temporary filters (not yet applied)
    const [tempFilters, setTempFilters] = useState({
        tags: [],
        brands: [],
        minPrice: '',
        maxPrice: '',
        search: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    const { increment } = useCart();

    useEffect(() => {
        getAllProducts();
    }, [activeFilters, pagination.current_page]);

    // Add this useEffect to filter products locally when filters change
    useEffect(() => {
        applyLocalFilters();
    }, [products, activeFilters]);

    const applyLocalFilters = () => {
        if (products.length === 0) return;

        let filtered = [...products];

        // Apply local filters (for tags and brands that are already loaded)
        if (activeFilters.tags.length > 0) {
            filtered = filtered.filter(product =>
                product.tags.some(tag => activeFilters.tags.includes(tag.name))
            );
        }

        if (activeFilters.brands.length > 0) {
            filtered = filtered.filter(product =>
                activeFilters.brands.includes(product.brand?.name)
            );
        }

        // Apply price range filter
        if (activeFilters.minPrice) {
            filtered = filtered.filter(product => product.price >= Number(activeFilters.minPrice));
        }

        if (activeFilters.maxPrice) {
            filtered = filtered.filter(product => product.price <= Number(activeFilters.maxPrice));
        }

        // Apply search filter
        if (activeFilters.search) {
            const searchTerm = activeFilters.search.toLowerCase();
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm) ||
                product.nameAr?.toLowerCase().includes(searchTerm) ||
                product.description?.toLowerCase().includes(searchTerm) ||
                product.descriptionAr?.toLowerCase().includes(searchTerm)
            );
        }

        // Apply sorting
        const sortOrder = activeFilters.sortOrder === 'desc' ? -1 : 1;
        filtered.sort((a, b) => {
            if (activeFilters.sortBy === 'price') {
                return (a.price - b.price) * sortOrder;
            } else if (activeFilters.sortBy === 'name') {
                return a.name.localeCompare(b.name) * sortOrder;
            } else {
                // Default sort by createdAt
                return (new Date(a.createdAt) - new Date(b.createdAt)) * sortOrder;
            }
        });

        setFilteredProducts(filtered);
    };

    const buildQueryString = () => {
        const params = new URLSearchParams();

        // Only send filters that require server-side processing
        if (activeFilters.search) {
            params.append('search', activeFilters.search);
        }

        if (activeFilters.minPrice) {
            params.append('min_price', activeFilters.minPrice);
        }

        if (activeFilters.maxPrice) {
            params.append('max_price', activeFilters.maxPrice);
        }

        params.append('sort_by', activeFilters.sortBy);
        params.append('sort_order', activeFilters.sortOrder);
        params.append('page', pagination.current_page);
        params.append('per_page', pagination.per_page);

        return params.toString();
    };

    const getAllProducts = async () => {
        try {
            setLoading(true);
            const queryString = buildQueryString();
            const response = await fetch(`${BASE_URL}api/v1/products?${queryString}`);

            if (!response.ok) {
                throw new Error('Failed to fetch products');
            }

            const data = await response.json();
            console.log("products", data);

            // Set the products from the API response
            setProducts(data.data);
            setPagination(data.pagination);

            // Extract unique tags and brands from ALL products (not just current page)
            const allTags = Array.from(
                new Set(data.data.flatMap(product => product.tags.map(tag => tag.name)))
            );
            const allBrands = Array.from(
                new Set(data.data.map(product => product.brand?.name).filter(Boolean))
            );

            setTags(allTags);
            setBrands(allBrands);

        } catch (err) {
            setError(err.message);
            console.error("Request failed:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleModal = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedProduct(null);
    };

    const handleAddToCart = (id) => {
        const product=products.find(p => p.id === id);
        // Calculate the actual price to use (discounted or original)
        const actualPrice = product.discount > 0
            ? product.price - (product.price * product.discount) / 100
            : product.price;
        console.log("act:",actualPrice);
        increment(product.id, actualPrice, showToast);
    };

    const handleTempFilterChange = (type, value) => {
        setTempFilters(prev => {
            const newFilters = { ...prev };

            if (type === 'tag') {
                newFilters.tags = newFilters.tags.includes(value)
                    ? newFilters.tags.filter(tag => tag !== value)
                    : [...newFilters.tags, value];
            }

            if (type === 'brand') {
                newFilters.brands = newFilters.brands.includes(value)
                    ? newFilters.brands.filter(brand => brand !== value)
                    : [...newFilters.brands, value];
            }

            if (type === 'minPrice') newFilters.minPrice = value;
            if (type === 'maxPrice') newFilters.maxPrice = value;
            if (type === 'search') newFilters.search = value;
            if (type === 'sortBy') newFilters.sortBy = value;
            if (type === 'sortOrder') newFilters.sortOrder = value;

            return newFilters;
        });
    };

    const applyFilters = () => {
        setActiveFilters({ ...tempFilters });
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const resetFilters = () => {
        const defaultFilters = {
            tags: [],
            brands: [],
            minPrice: '',
            maxPrice: '',
            search: '',
            sortBy: 'createdAt',
            sortOrder: 'desc'
        };

        setTempFilters(defaultFilters);
        setActiveFilters(defaultFilters);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const renderPagination = () => {
        if (pagination.last_page <= 1) return null;

        const pages = [];
        const startPage = Math.max(1, pagination.current_page - 2);
        const endPage = Math.min(pagination.last_page, pagination.current_page + 2);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${pagination.current_page === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Product pagination">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(pagination.current_page - 1)}
                            disabled={pagination.current_page === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {pages}
                    <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(pagination.current_page + 1)}
                            disabled={pagination.current_page === pagination.last_page}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (loading && pagination.current_page === 1) return <div className="text-center" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <ScaleLoader color="#7a2cc7" loading={true} />
    </div>;
    if (error) return <div className="text-center py-5">Error: {error}</div>;

    return (
        <div className="shop-page">
            <div id="shop-store" className="shop-store container section-padding mixit_active">
                <h2 className="text-color-bright mb-0 fs-1 text-center">{t('shop')}</h2>
                <p className="text-muted-color text-center">
                    {t('more than 100+ products from best brands for you')}
                </p>

                {/* Enhanced Filters */}
                <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('search products...')}
                            value={tempFilters.search}
                            onChange={(e) => handleTempFilterChange('search', e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 mb-3">
                        <select
                            className="form-select"
                            value={tempFilters.sortBy}
                            onChange={(e) => handleTempFilterChange('sortBy', e.target.value)}
                        >
                            <option value="createdAt">{t('Newest')}</option>
                            <option value="price">{t('Price')}</option>
                            <option value="name">{t('Name')}</option>
                        </select>
                    </div>
                    <div className="col-md-3 mb-3">
                        <select
                            className="form-select"
                            value={tempFilters.sortOrder}
                            onChange={(e) => handleTempFilterChange('sortOrder', e.target.value)}
                        >
                            <option value="desc">{t('Descending')}</option>
                            <option value="asc">{t('Ascending')}</option>
                        </select>
                    </div>
                </div>

                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <input
                            type="number"
                            className="form-control"
                            placeholder={t('Min Price')}
                            value={tempFilters.minPrice}
                            onChange={(e) => handleTempFilterChange('minPrice', e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 mb-3">
                        <input
                            type="number"
                            className="form-control"
                            placeholder={t('Max Price')}
                            value={tempFilters.maxPrice}
                            onChange={(e) => handleTempFilterChange('maxPrice', e.target.value)}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <div className="d-flex gap-2 flex-wrap">
                            {/* Tags Filter Badges */}
                            {tags.slice(0, 5).map(tag => (
                                <button
                                    key={tag}
                                    className={`btn btn-sm ${tempFilters.tags.includes(tag) ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleTempFilterChange('tag', tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Apply and Reset buttons */}
                <div className="row mb-4">
                    <div className="col-12 d-flex justify-content-end gap-2">
                        <button
                            className="btn btn-secondary"
                            onClick={resetFilters}
                        >
                            {t('Reset')}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={applyFilters}
                        >
                            {t('Apply Filters')}
                        </button>
                    </div>
                </div>

                {/* Products Grid - Now using filteredProducts */}
                <div id="shop-store-wrapper" className="row gy-4 gx-3 mb-4">
                    {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => {
                            const discountedPrice = product.discount > 0
                                ? product.price - (product.price * product.discount) / 100
                                : product.price;

                            return (
                                <div key={product.id} className="col-6 col-md-4 col-lg-3">
                                    <div className="mix-box">
                                        {product.discount > 0 && (
                                            <div className="has-offer">
                                                Special Offer
                                            </div>
                                        )}
                                        <div className="mix-img">
                                            <img
                                                src={`https://akadgatepharmacy.com/backend/storage/app/public${product.featuredImage.replace("/storage",'')}`}
                                                alt={product.name}
                                                className="img-fluid"
                                                loading="lazy"
                                            />
                                            <div className="view-img d-none d-lg-grid">
                                                <button
                                                    type="button"
                                                    onClick={() => handleModal(product)}
                                                    className="bg-transparent p-0 border-0"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="icon icon-tabler icon-tabler-eye"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth="1.5"
                                                        stroke="#6f32be"
                                                        fill="none"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                        <path
                                                            d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="mix-details">
                                            <h5>{product.name}</h5>
                                            <h6 className="mb-0">
                                                <span className={`me-2 fw-bold ${product.discount > 0 && "text-decoration-line-through"}`}>
                                                    {product.price.toLocaleString()} IQD
                                                </span>
                                                {product.discount > 0 && (
                                                    <span className="fw-bold">
                                                        {discountedPrice.toLocaleString()} IQD
                                                    </span>
                                                )}
                                            </h6>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleAddToCart(product.id)}
                                            className="btn mix-add-btn-lg w-100 d-none d-lg-flex"
                                        >
                                            <span>Add To Cart</span>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="icon icon-tabler icon-tabler-shopping-bag-plus"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="#ececec"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path
                                                    d="M12.5 21h-3.926a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304h11.339a2 2 0 0 1 1.977 2.304l-.263 1.708"
                                                />
                                                <path d="M16 19h6" />
                                                <path d="M19 16v6" />
                                                <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
                                            </svg>
                                        </button>
                                        <div className="mix-btns d-lg-none">
                                            <button
                                                type="button"
                                                onClick={() => handleModal(product)}
                                                className="btn mix-view-btn"
                                            >
                                                <span>View</span>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="icon icon-tabler icon-tabler-eye"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="#6f32be"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path
                                                        d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleAddToCart(product.id)}
                                                className="btn mix-add-btn"
                                            >
                                                <span>Add</span>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="icon icon-tabler icon-tabler-shopping-bag-plus"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="#6f32be"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path
                                                        d="M12.5 21h-3.926a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304h11.339a2 2 0 0 1 1.977 2.304l-.263 1.708"
                                                    />
                                                    <path d="M16 19h6" />
                                                    <path d="M19 16v6" />
                                                    <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 text-center py-5">
                            {activeFilters.tags.length > 0 || activeFilters.brands.length > 0 || activeFilters.search
                                ? "No products match your filters"
                                : "No Products Available"
                            }
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {renderPagination()}

                {/* Results info */}
                <div className="text-center text-muted mt-3">
                    Showing {filteredProducts.length} of {pagination.total} products
                </div>
            </div>

            <PaymentMethods />
            <Brands />
            <ContactDetails/>
            {showModal && (
                <ProductModal product={selectedProduct} showToast={showToast} onClose={closeModal} />
            )}
        </div>
    );
};

export default Shop;